import { useThree } from '@react-three/fiber';
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { throttle, round } from 'lodash-es';
import { useEditorStore } from 'modules/editor/store';

export function PointerCoordinates() {
  const { camera, scene, pointer, gl, raycaster } = useThree();
  const isPointerActive = useRef(true);

  const onPointerMoveThrottled = useMemo(() => {
    const onPointerMove = () => {
      raycaster.setFromCamera(pointer, camera);
      const intersects = raycaster.intersectObject(scene);

      if (intersects.length > 0 && isPointerActive.current) {
        const { x, y } = intersects[0].point;

        useEditorStore.setState({ pointerPosition: { x: round(x, 2), y: round(y, 2) } });
      }
    };

    return throttle(onPointerMove, 200, { leading: true, trailing: true });
  }, [camera, pointer, raycaster, scene]);

  const handlePointerLeave = useCallback(() => {
    isPointerActive.current = false;
    useEditorStore.setState({ pointerPosition: null });
  }, []);

  const handlePointerEnter = useCallback(() => {
    isPointerActive.current = true;
  }, []);

  useEffect(() => {
    gl.domElement.addEventListener('pointermove', onPointerMoveThrottled);
    gl.domElement.addEventListener('pointerenter', handlePointerEnter);
    gl.domElement.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      gl.domElement.removeEventListener('pointermove', onPointerMoveThrottled);
      gl.domElement.removeEventListener('pointerenter', handlePointerEnter);
      gl.domElement.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [gl.domElement, onPointerMoveThrottled, handlePointerLeave, handlePointerEnter]);

  return null;
}
